import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Cancel from "@mui/icons-material/Cancel";
import { MRT_Column } from "material-react-table";
import { SyntheticEvent } from "react";

import { ProspectRow } from "../types";

type Props = {
  column: MRT_Column<ProspectRow>;
  options: string[];
  label: string;
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === "string");

export function MultiSelectFilter({ column, options, label }: Props) {
  const filterValue = column.getFilterValue();
  const selected = isStringArray(filterValue) ? filterValue : [];

  const handleChange = (_event: SyntheticEvent, nextValue: string[]) => {
    column.setFilterValue(nextValue.length > 0 ? nextValue : undefined);
  };

  const handleDelete = (valueToRemove: string) => {
    const next = selected.filter((entry) => entry !== valueToRemove);
    column.setFilterValue(next.length > 0 ? next : undefined);
  };

  return (
    <Autocomplete<string, true>
      multiple
      disableCloseOnSelect
      options={options}
      value={selected}
      onChange={handleChange}
      size="small"
      clearIcon={null}
      ListboxProps={{ sx: { maxHeight: 320 } }}
      isOptionEqualToValue={(option, value) => option === value}
      noOptionsText="No options"
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={selected.length === 0 ? `Filter by ${label}` : ""}
          inputProps={{
            ...params.inputProps,
            "aria-label": `Filter by ${label}`,
          }}
        />
      )}
      renderOption={(props, option, { selected: isSelected }) => (
        <li {...props} key={option}>
          <Checkbox
            checked={isSelected}
            size="small"
            sx={{ py: 0, mr: 1 }}
            disableRipple
          />
          <ListItemText
            primary={option}
            primaryTypographyProps={{ variant: "body2" }}
          />
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={key ?? option}
              label={option}
              title={option}
              size="small"
              color="secondary"
              onDelete={() => handleDelete(option)}
              deleteIcon={
                <Cancel onMouseDown={(event) => event.stopPropagation()} />
              }
              sx={{
                maxWidth: "100%",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          );
        })
      }
      sx={{ minWidth: 160, width: "100%" }}
    />
  );
}
